import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Globe, Search } from 'lucide-react';
import { useLocalization } from '../contexts/LocalizationContext';
import CountryFlag from './CountryFlag';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const SEARCH_THRESHOLD = 6;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// variant: 'footer' (on the dark footer) | 'header' (compact, desktop header; `light` = over the hero image)
const CountrySelector = ({ variant = 'footer', light = false }) => {
  const { t, i18n } = useTranslation();
  const { country, availableCountries, changeCountry } = useLocalization();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const names = useMemo(() => {
    const lang = i18n.language || 'en';
    try {
      return {
        region: new Intl.DisplayNames([lang], { type: 'region' }),
        language: new Intl.DisplayNames([lang], { type: 'language' }),
      };
    } catch (e) {
      return null;
    }
  }, [i18n.language]);

  const countryName = (c) => names?.region.of(c.code) || c.name;
  const languageName = (c) => capitalize(names?.language.of(c.language)) || c.languageName;

  const showSearch = availableCountries.length > SEARCH_THRESHOLD;
  const filtered = availableCountries.filter((c) => {
    const q = query.trim().toLowerCase();
    return !q || [countryName(c), c.name, languageName(c), c.currency].some((v) => (v || '').toLowerCase().includes(q));
  });

  if (!country) return null;

  const select = (code) => {
    setIsOpen(false);
    if (code !== country.code) changeCountry(code);
  };

  const label = t('countrySelector.changeCountry', { country: countryName(country), currency: country.currency });

  const trigger =
    variant === 'header' ? (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={label}
        title={label}
        data-testid="country-selector-trigger"
        className="flex items-center gap-2 rounded-full px-2.5 py-1.5 transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be1f1a]"
        style={{ color: light ? '#fff' : 'var(--ink)', fontSize: '12px', letterSpacing: '1px', fontWeight: 500 }}
      >
        <CountryFlag code={country.code} size={20} />
        <span>{country.currency}</span>
        <ChevronDown className="h-3 w-3 opacity-60" strokeWidth={2} />
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={label}
        data-testid="country-selector-trigger"
        className="flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#d8d2c5' }}
      >
        <CountryFlag code={country.code} size={18} />
        <span>{countryName(country)}</span>
        <span style={{ opacity: 0.6 }}>· {country.currency}</span>
      </button>
    );

  return (
    <>
      {trigger}

      <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) setQuery(''); }}>
        <DialogContent
          className="w-[min(92vw,30rem)] max-w-none gap-0 overflow-hidden rounded-2xl p-0 sm:rounded-2xl"
          data-testid="country-selector-dialog"
        >
          <DialogHeader className="space-y-2 px-8 pb-5 pt-8 text-left">
            <div
              className="mb-1 flex h-10 w-10 items-center justify-center rounded-full"
              style={{ background: 'var(--bg-alt, #f5f0e8)', color: 'var(--terracotta, #be1f1a)' }}
            >
              <Globe className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <DialogTitle style={{ fontFamily: 'var(--font-display)', fontSize: '26px', fontWeight: 400, letterSpacing: '-0.3px', lineHeight: 1.15 }}>
              {t('countrySelector.title')}
            </DialogTitle>
            <DialogDescription style={{ fontSize: '14px', lineHeight: 1.5 }}>
              {t('countrySelector.description')}
            </DialogDescription>
          </DialogHeader>

          {showSearch && (
            <div className="relative px-8 pb-3">
              <Search className="pointer-events-none absolute left-11 top-1/2 h-4 w-4 -translate-y-[70%] text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('countrySelector.search')}
                aria-label={t('countrySelector.search')}
                className="h-11 w-full rounded-lg border border-gray-300 pl-10 pr-3 text-sm focus:border-[#be1f1a] focus:outline-none focus:ring-1 focus:ring-[#be1f1a]"
              />
            </div>
          )}

          <div
            role="radiogroup"
            aria-label={t('countrySelector.title')}
            className="max-h-[50vh] space-y-3 overflow-y-auto px-8 pb-6 pt-1"
          >
            {filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">{t('countrySelector.noResults')}</p>
            ) : (
              filtered.map((c) => {
                const selected = c.code === country.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => select(c.code)}
                    data-testid={`country-option-${c.code}`}
                    className="flex w-full items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#be1f1a] hover:border-[#be1f1a] hover:shadow-sm"
                    style={{
                      borderColor: selected ? 'var(--terracotta, #be1f1a)' : 'var(--line, #e5dfd3)',
                      background: selected ? 'rgba(190,31,26,0.04)' : '#fff',
                    }}
                  >
                    <CountryFlag code={c.code} size={36} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-900" style={{ fontSize: '15px' }}>
                        {countryName(c)}
                      </span>
                      <span className="block truncate text-sm text-gray-500">
                        {languageName(c)} · {c.currency} ({c.currencySymbol})
                      </span>
                    </span>
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                      style={{ background: selected ? 'var(--terracotta, #be1f1a)' : 'transparent', border: selected ? 'none' : '1px solid #d4cfc4' }}
                    >
                      {selected && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <p className="border-t px-8 py-4 text-xs text-gray-500" style={{ background: 'var(--bg-alt, #faf8f3)', borderColor: 'var(--line, #e5dfd3)' }}>
            {t('countrySelector.note')}
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CountrySelector;
